import { z } from "zod";

/**
 * Lifecycle position of one background task as reported to the frontend.
 *
 * `pending` and `paused` are non-terminal — a paused task resumes, a pending
 * one has not started work yet. `completed` / `failed` / `stopped` are the
 * three terminal positions.
 *
 * Declared here, in the frontend wire contract, rather than shared with the
 * runner IPC enum it currently mirrors. The two are separate promises to
 * separate audiences: widening what the runner may report is a sandbox-side
 * decision, and widening what the frontend is promised is a product one. Kept
 * apart, the host translator fails to compile the moment they diverge, which
 * is where a human gets asked whether the frontend contract widens too.
 */
export const ChatStreamBackgroundTaskStatusSchema = z.enum([
  "pending",
  "running",
  "paused",
  "completed",
  "failed",
  "stopped"
]);

export type ChatStreamBackgroundTaskStatus = z.infer<typeof ChatStreamBackgroundTaskStatusSchema>;

/** Cumulative cost of one background task, as last reported by the runner. */
export const ChatStreamBackgroundTaskUsageSchema = z.object({
  totalTokens: z.number().int().nonnegative(),
  toolUses: z.number().int().nonnegative(),
  durationMs: z.number().nonnegative()
});

export type ChatStreamBackgroundTaskUsage = z.infer<typeof ChatStreamBackgroundTaskUsageSchema>;

/**
 * Snapshot of ONE background task's current state, forwarded field-for-field
 * from the agent runner's `background_task_state` IPC event.
 *
 * State, not edges: every emission carries the runner's complete current
 * knowledge of that task, so a consumer keyed on `taskId` reaches the right
 * state by overwriting whatever it held — a dropped or duplicated snapshot
 * costs staleness, never a wedged indicator.
 *
 * Self-contained: the event opens no block and awaits no settlement, so it is
 * a safe replay boundary anywhere in the log.
 *
 * **Nothing here reaches Postgres, and every field below is live-only.**
 * `isLeadTurnContentEvent` answers `false` for this event, so it passes neither
 * the turn accumulator nor the durable-stream materializer, and the database
 * carries no background-task table for it to land in. What a reopened
 * conversation gets instead is the per-conversation listing cache — bounded by
 * its own expiry and gated on the runner still being alive — read back through
 * the listing endpoint's response shape, which names the fields it exposes one
 * at a time. So a field added here reaches a returning reader only if that
 * response shape and the browser's seed schema are widened with it, and a field
 * left out of them is correct on the wire and absent the moment the tab
 * reloads.
 *
 * `toolUseId` joins the task back to the agent's own thread. It is absent
 * until the runner observes a signal carrying one — genuinely so for a
 * foreground agent backgrounded mid-flight — after which the runner carries it
 * on every later snapshot.
 *
 * A terminal `status` states how the task ended and comes only from the SDK's
 * own end-of-task bookend. Membership changes report that a task is no longer
 * live, never how it finished.
 *
 * `startedAt` is when the HOST first saw this task, minted from the host's own
 * clock and repeated unchanged on every later snapshot for that task. It is a
 * host-observed fact rather than anything the runner reports: the SDK publishes
 * no start time on any task message, so there is no field being forwarded here.
 *
 * The host's clock rather than the sandbox's, because a reader subtracts this
 * from its own `Date.now()` — stamping inside the sandbox would put two
 * unsynchronised machines either side of that subtraction, and the failure mode
 * is a negative elapsed time reaching a user. Stamping on receipt costs the
 * transit time, tens of milliseconds against a figure rendered to the second.
 *
 * Optional because a snapshot stored before this field existed decodes without
 * it, and a reader that has none renders no elapsed figure rather than a wrong
 * one.
 *
 * `endTime` is the opposite of `startedAt` in the one way that matters: it is
 * the SANDBOX's wall clock, forwarded from the SDK rather than minted here. The
 * two are therefore not a pair, and `endTime - startedAt` is a subtraction
 * across two unsynchronised machines — the failure the paragraph above exists to
 * prevent, arrived at from the other side. A finished task's measured duration
 * is `usage.durationMs`; a running task's elapsed figure is `Date.now()` minus
 * `startedAt`, both clocks being the reader's own.
 *
 * `workflowName` labels a workflow task and nothing else — the SDK sets it only
 * when the task kind is `local_workflow`, where it is the only thing that can
 * tell two apart. `prompt` is what a spawned subagent was asked to do, which
 * `description` only summarises. Neither is rendered yet; both are carried so
 * the decision about what a row shows can be taken without a wire change.
 *
 * `skipTranscript` is the one field here that deliberately stops short of that.
 * The SDK marks housekeeping tasks with it and asks hosts to keep them out of
 * activity indicators, which is a change to what the listing counts as work —
 * so it is a behaviour decision rather than a field to plumb, and it belongs
 * with whichever change takes it. It is therefore absent from the listing
 * endpoint's response shape and from the browser's seed schema, and a reopened
 * conversation does not carry it.
 *
 * `startedAtSessionId` names the runner session `startedAt` was minted in, and
 * exists so a stored snapshot can be told apart from one belonging to a
 * different session under the same conversation and task id. It rides this row
 * rather than earning a kind of its own because it is a fact about this task's
 * start — the row a reader already holds when it needs it. `agentColour` on
 * `agent-ipc-v2/events/messages/assistant-tool-call.event.ts` is the worked case.
 *
 * It is the SDK's own session id, observed on the runner's `system_init` line,
 * and NEVER the `agentSessionId` column. That column has a second producer: it
 * is nulled alongside `sandboxStatus: "EXPIRED"` when a sandbox is not found,
 * so a null there means either "never set" or "sandbox gone" with nothing to
 * tell the two apart. Reading the wire keeps one producer and one meaning.
 *
 * A reader renders nothing from it. It is written and read by the host alone,
 * to decide whether a stored start time may be adopted, so it crosses no hop to
 * the browser and the listing DTO's field allowlist keeps it that way.
 *
 * Optional for the same reason `startedAt` is, plus one more: a process that
 * has not yet seen a `system_init` for a conversation has no session to name,
 * and that absence is a distinct state from a mismatch — see the details
 * adapter, which preserves rather than re-mints when it cannot tell.
 *
 * @see packages/types/src/shared/streaming/agent-ipc-v2/events/messages/task-snapshot.event.ts
 */
export const TaskSnapshotStreamEventSchema = z.object({
  type: z.literal("background-task-state"),
  taskId: z.string().min(1),
  status: ChatStreamBackgroundTaskStatusSchema,
  toolUseId: z.string().min(1).optional(),
  taskKind: z.string().min(1).optional(),
  workflowName: z.string().min(1).optional(),
  description: z.string().optional(),
  prompt: z.string().optional(),
  subagentType: z.string().min(1).optional(),
  skipTranscript: z.boolean().optional(),
  lastToolName: z.string().min(1).optional(),
  summary: z.string().optional(),
  outputFile: z.string().min(1).optional(),
  error: z.string().optional(),
  isBackgrounded: z.boolean().optional(),
  usage: ChatStreamBackgroundTaskUsageSchema.optional(),
  startedAt: z.number().int().positive().optional(),
  endTime: z.number().int().nonnegative().optional(),
  startedAtSessionId: z.string().min(1).optional(),
  /**
   * The dispatching call's own `parent_tool_use_id`, forwarded from the
   * runner's IPC event — present only when the Task/Agent call that started
   * this task was itself made from inside another agent's thread. Absent
   * when the lead made the call directly.
   */
  dispatchParentToolUseId: z.string().min(1).optional(),
  /**
   * The name the runner resolved for this task's dispatching call, forwarded
   * from the runner's IPC event and canonicalized by the host translator —
   * see `toCanonicalAgentName` in `@nexus/types`.
   */
  dispatchName: z.string().min(1).optional(),
  /**
   * Whether the dispatching call asked for the agent to run in the
   * background, forwarded from the runner's IPC event. A foreground subagent
   * emits the same task signals as a backgrounded one, so a consumer that
   * acts only on background agents reads this rather than the task's kind.
   * Absent from a snapshot a runner predating this field emitted.
   */
  dispatchBackgrounded: z.boolean().optional()
});

/** The `taskKind` value marking a background task as an agent, as opposed to a bash or workflow task. */
export const BACKGROUND_TASK_KIND_AGENT = "local_agent";

/** The three terminal positions `ChatStreamBackgroundTaskStatusSchema` declares — a task past one of these will not change status again. */
export const TERMINAL_BACKGROUND_TASK_STATUSES = ["completed", "failed", "stopped"] as const;

export type TerminalBackgroundTaskStatus = (typeof TERMINAL_BACKGROUND_TASK_STATUSES)[number];

/** Narrows a `ChatStreamBackgroundTaskStatus` to the three terminal positions. */
export function isTerminalBackgroundTaskStatus(
  status: string
): status is TerminalBackgroundTaskStatus {
  return (TERMINAL_BACKGROUND_TASK_STATUSES as readonly string[]).includes(status);
}

/**
 * The COMPLETE set of background tasks running for this conversation, emitted
 * on every membership change.
 *
 * Replace semantics — `taskIds` is the whole live set, so a consumer keyed on
 * membership overwrites what it holds. This event alone decides who is
 * running; `background-task-state` carries per-task detail and enriches only.
 * Splitting them that way is what keeps a missed per-task edge costing a stale
 * field rather than a task wedged as running forever.
 *
 * `emittedAt` is a strictly-increasing millisecond stamp minted by the runner.
 * A consumer applying payloads out of order keeps the highest stamp and
 * discards older ones, so a delayed payload can never resurrect a task a newer
 * one dropped.
 *
 * Self-contained: the event opens no block and awaits no settlement, so it is
 * a safe replay boundary anywhere in the log.
 *
 * Declared here rather than shared with the runner IPC event it mirrors, for
 * the same reason the background-task status enum is: the two contracts answer
 * to different audiences, and the host translator is where a divergence has to
 * be decided rather than absorbed.
 */
export const BackgroundTasksLevelStreamEventSchema = z.object({
  type: z.literal("background-tasks-level"),
  taskIds: z.array(z.string().min(1)),
  emittedAt: z.number().int().positive()
});

/**
 * The SDK's own turn-over signal, forwarded verbatim from the runner.
 * `state: 'idle'` marks the turn over, and only that. Measured on this
 * transport it lands in the same millisecond as the turn's own result, and
 * leads the background-task level emptying by as much as two minutes — so it
 * says nothing about whether the session's work is finished, and a consumer
 * pairs it with that level rather than reading it as quiescence.
 *
 * An enhancement only: emission is off by default and the flag arming it
 * appears in no published SDK type, so a consumer that requires this event
 * would never resolve.
 */
export const SessionStateChangedStreamEventSchema = z.object({
  type: z.literal("session-state-changed"),
  state: z.enum(["idle", "running", "requires_action"])
});
